/**
 * `<ChatMessageList>` — Architect-visual message list primitive (Y bundle / spec §10.1.a).
 *
 * Renders each message as a row with role meta + bubble in the Architect
 * `opAiChat*` family — kills the per-surface visual drift catalogued in the
 * Phase 0 audit (§3.4: 4 CSS class families before this).
 *
 * Assistant messages render via `ReactMarkdown` + `opAiChatMarkdownComponents`
 * so prose, lists, fenced code, etc. all look right. User messages render
 * as plain text (intentional — user input is verbatim, no markdown
 * interpretation desired).
 *
 * **Per-message artifact slot.** Surfaces with rich artifacts (recommendation
 * cards, suggested SQL, applied chart pills) supply a `renderAssistantExtras`
 * callback that returns surface-specific JSX appended below the bubble's
 * markdown body. Returning null skips the slot for that message.
 *
 * Why this is the boundary between primitives and surface code: the bubble +
 * markdown rendering is uniform across surfaces (Y aim), but artifacts vary
 * (chart spec preview is reactive-viz-specific; recommendation card is
 * Architect-specific; etc.). Letting each surface render its own artifacts
 * keeps the primitive small and focused.
 */

import { memo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { opAiChatMarkdownComponents } from "../../lib/opAiChatMarkdown.js";
import type { AiChatMessage } from "../../hooks/useAiChatThread.js";

// Hoisted to module scope so the array identity is stable across renders —
// a fresh `[remarkGfm]` literal on every render would defeat ReactMarkdown's
// internal prop memoization.
const REMARK_PLUGINS = [remarkGfm];

/**
 * Assistant markdown body, memoized on `content`. remark-gfm parsing (string →
 * mdast → React tree) is expensive and was previously re-run for EVERY message
 * on every parent render. Chat surfaces (e.g. the transform inspector) live
 * inside components that re-render on nearly every interaction — keystrokes,
 * autosave, resize — so a long history re-parsed all of itself each time,
 * scaling lag with conversation length. A persisted message's content never
 * changes, so memoizing on `content` means each bubble parses exactly once.
 */
const AssistantMarkdown = memo(function AssistantMarkdown({ content }: { content: string }): JSX.Element {
  return (
    <div className="opAiChatProse">
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={opAiChatMarkdownComponents}>{content}</ReactMarkdown>
    </div>
  );
});

export type ChatMessageListProps<T extends AiChatMessage = AiChatMessage> = {
  messages: T[];
  /** Label shown above assistant bubbles. Per-surface (e.g. "Atlas TanTable agent"). */
  assistantLabel: string;
  /** Optional per-message extras rendered below the bubble's markdown body. */
  renderAssistantExtras?: (message: T) => ReactNode;
  /**
   * Optional empty-state node when there are no messages. Surfaces typically
   * render a one-line italic prompt suggestion here ("Tell me what chart…").
   */
  emptyState?: ReactNode;
  /** Optional trailing node (used for ChatThinkingIndicator + ChatToolPulse). */
  trailingNode?: ReactNode;
};

export function ChatMessageList<T extends AiChatMessage = AiChatMessage>(
  props: ChatMessageListProps<T>
): JSX.Element {
  const { messages, assistantLabel, renderAssistantExtras, emptyState, trailingNode } = props;
  return (
    <div className="opAiChatThread">
      {messages.length === 0 && emptyState ? emptyState : null}
      {messages
        .filter((m) => m.role !== "system")
        .map((m) => {
          if (m.role === "user") {
            return (
              <div key={m.id} className="opAiChatRow opAiChatRowUser">
                <div className="opAiChatMeta">You</div>
                <div className="opAiChatBubbleUser">
                  <div className="opAiChatProse" style={{ whiteSpace: "pre-wrap" }}>
                    {m.content}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className="opAiChatRow opAiChatRowAssistant">
              <div className="opAiChatMeta">{assistantLabel}</div>
              <div className="opAiChatBubbleAssistant">
                <AssistantMarkdown content={m.content} />
                {renderAssistantExtras ? renderAssistantExtras(m) : null}
              </div>
            </div>
          );
        })}
      {trailingNode}
    </div>
  );
}
